import React, { useEffect, useRef, useState } from 'react'
import {
    BlueConicIntegrationStoreConfiguration,
    CreateBlueConicDomainIntegrationRequest,
    UpdateBlueConicDomainIntegrationRequest,
    UpdateBlueConicIntegrationRequest,
} from '@pushly/models/lib/structs/domains'
import { useDebouncer } from '@pushly/aqe/lib/hooks'
import {
    CloseCircleFilled,
    DeleteFilled,
    EditOutlined,
    InfoCircleOutlined,
    LinkOutlined,
    PlusCircleFilled,
} from '@ant-design/icons'
import { objectFromEntries } from '../../../../_utils/object'
import { getRandId } from '@pushly/models/lib/utils/get-rand-id'
import { PlatformIntegrationModal } from '../../../platform-integrations/platform-integration-modal/platform-integration-modal'
import { BlueConicLogo } from '../../../icons/blueconic-logo'
import { extractFriendlyErrorMessage } from '../../../../exceptions/helpers'
import classnames from 'classnames'
import { isSimpleFormError, SimpleFormError, simpleFormErrorNotification } from '../../../../_utils/utils'
import { SizeType } from 'antd/lib/config-provider/SizeContext'
import { FormInstance } from 'antd/es/form'
import { streamApplicationError } from '../../../../_utils/airbrake'
import { InvalidFieldException } from '@pushly/models/lib/exceptions/invalid-field-exception'
import { MissingFieldException } from '@pushly/models/lib/exceptions/missing-field-exception'
import { flatMap } from '../../../../_utils/array'
import { Button, Flex, Form, Input, InputRef, Popover, Space, Tooltip, Typography } from 'antd'
import { Well } from '@pushly/aqe/lib/components'

type EditorRequest = CreateBlueConicDomainIntegrationRequest | UpdateBlueConicDomainIntegrationRequest

type BaseBlueConicIntegrationEditorProps = {
    mode?: 'add' | 'edit'
    source?: UpdateBlueConicDomainIntegrationRequest | CreateBlueConicDomainIntegrationRequest
    processing?: boolean
    onSave: (request: EditorRequest) => void
    onCancel: () => void
}

type AddBlueConicIntegrationEditorProps = BaseBlueConicIntegrationEditorProps & {
    mode: 'add'
    source: CreateBlueConicDomainIntegrationRequest
    onSave: (request: CreateBlueConicDomainIntegrationRequest) => void
    onCancel: () => void
}

type EditBlueConicIntegrationEditorProps = BaseBlueConicIntegrationEditorProps & {
    mode: 'edit'
    source: UpdateBlueConicDomainIntegrationRequest
    onSave: (request: UpdateBlueConicDomainIntegrationRequest) => void
    onCancel: () => void
}

type BlueConicIntegrationEditorProps =
    | BaseBlueConicIntegrationEditorProps
    | AddBlueConicIntegrationEditorProps
    | EditBlueConicIntegrationEditorProps

type StoreIdRecord = { storeId: string; record: BlueConicIntegrationStoreConfiguration }
type StoreIdRecordMap = Map<string, StoreIdRecord>

type FormItemIdSuffix = 'id' | 'name'

const INTEGRATION_MODAL_COMPONENT_SIZE: SizeType = 'middle'
const MAX_STORES = 10

/**
 * Friendly error field names map for BlueConic model validation errors
 */
const blueConicDomainIntegrationErrorFieldNamesMap = {
    data: '',
    'data.name': 'Application Name',
    'data.client_id': 'Application Client ID',
    'data.client_secret': 'Application Client Secret',
    'data.tenant_name': 'Tenant Name',
    'data.client_stores': 'Associated Content Stores',
}

export const BlueConicIntegrationEditor: React.FC<BlueConicIntegrationEditorProps> = (props) => {
    let requestSource: EditorRequest | undefined = props.source?.clone()

    const [debounceDomAction] = useDebouncer(50)
    const [formInstance] = Form.useForm()

    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [workingRequest, setWorkingRequest] = useState<EditorRequest | undefined>(requestSource)
    const [workingStores, setWorkingStores] = useState<StoreIdRecordMap>(
        extractStoreIdRecordMap(requestSource?.data?.stores),
    )

    // Update the working request and stores when the source changes.
    useEffect(() => {
        setWorkingRequest(requestSource)
        setWorkingStores(extractStoreIdRecordMap(requestSource?.data?.stores))
        setSubmitAttempted(false)
        formInstance.resetFields()
    }, [!!requestSource])

    const storesListEndRef = useRef<HTMLButtonElement>(null)
    const lastStoreRecordRef = useRef<InputRef>(null)

    const mode = props.mode ?? (requestSource ? 'edit' : 'add')
    const hasMaxStoreIds = workingStores.size >= MAX_STORES

    const validateStoreFormFields = (fields: FormItemIdSuffix[] | FormItemIdSuffix) => {
        formInstance.validateFields(getFormItemIdsFromStores(workingStores, Array.isArray(fields) ? fields : [fields]))
    }

    return (
        <PlatformIntegrationModal
            className="blueconic-integration-editor"
            title={`${mode === 'add' ? 'Create' : 'Edit'} Integration`}
            titleIcon={mode === 'add' ? <LinkOutlined /> : <EditOutlined />}
            providerLogo={<BlueConicLogo />}
            open={!!props.source}
            okText="Save"
            okButtonProps={{ loading: props.processing }}
            onOk={async () => {
                if (!workingRequest) return

                try {
                    props.onSave(await compileSavePayload(mode, formInstance, workingRequest, workingStores))
                } catch (err) {
                    if (isSimpleFormError(err)) {
                        simpleFormErrorNotification(err)
                        return
                    }

                    streamApplicationError(err, {
                        detailedName: 'blueconic_integration_editor_modal_submit_error',
                    })
                } finally {
                    setSubmitAttempted(true)
                }
            }}
            onCancel={props.onCancel}
        >
            {mode === 'add' && (
                <div className="blueconic-integration-editor-add-instructions">
                    <p>
                        Connect your BlueConic and Pushly data to drive personalized subscriber experiences through
                        Journeys content recommendations.
                    </p>
                </div>
            )}

            <Form
                form={formInstance}
                layout="vertical"
                size={INTEGRATION_MODAL_COMPONENT_SIZE}
                requiredMark={false}
                validateTrigger={submitAttempted ? ['onBlur'] : false}
            >
                <Form.Item
                    label={
                        <Space size={4}>
                            <span>Tenant Name</span>
                            <Popover
                                classNames={{
                                    content: 'blueconic-editor-field-popover',
                                }}
                                content={
                                    <span>
                                        The most common way to find your tenant name is to look at your BlueConic login
                                        URL. It will usually be in the format{' '}
                                        <span className="example-tenant-name">
                                            https://[your-tenant-name].blueconic.net/login
                                        </span>
                                        .
                                    </span>
                                }
                            >
                                <InfoCircleOutlined className="info-icon" />
                            </Popover>
                        </Space>
                    }
                >
                    <Space.Compact block>
                        <Form.Item
                            noStyle
                            name="tenant_name"
                            initialValue={workingRequest?.data?.tenant_name}
                            rules={[
                                { required: mode === 'add', message: 'Tenant Name is required' },
                                { min: 1, message: 'Tenant Name must not be empty' },
                            ]}
                        >
                            <Input
                                placeholder="mycompany"
                                onChange={(ev) => {
                                    if (!workingRequest) return
                                    const update = workingRequest.clone()
                                    update.data!.tenant_name = ev.target.value
                                    setWorkingRequest(update)
                                }}
                            />
                        </Form.Item>
                        <Space.Addon>
                            <span>.blueconic.net</span>
                        </Space.Addon>
                    </Space.Compact>
                </Form.Item>

                <Form.Item
                    name="name"
                    label={
                        <Space size={4}>
                            <span>Application Name</span>
                            <Popover
                                classNames={{
                                    content: 'blueconic-editor-field-popover',
                                }}
                                content={
                                    <span>
                                        Enter the name of the BlueConic application you created for this integration.
                                    </span>
                                }
                            >
                                <InfoCircleOutlined className="info-icon" />
                            </Popover>
                        </Space>
                    }
                    initialValue={workingRequest?.data?.name}
                    rules={[
                        { required: mode === 'add', message: 'Application Name is required' },
                        { min: 1, message: 'Application Name must not be empty' },
                    ]}
                >
                    <Input
                        data-1p-ignore
                        placeholder="Pushly"
                        onChange={(ev) => {
                            if (!workingRequest) return
                            const update = workingRequest.clone()
                            update.data!.name = ev.target.value
                            setWorkingRequest(update)
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="client_id"
                    label={
                        <Space size={4}>
                            <span>Application Client ID</span>
                            <Popover
                                classNames={{
                                    content: 'blueconic-editor-field-popover',
                                }}
                                content={<span>Paste the Client ID from your BlueConic application's settings.</span>}
                            >
                                <InfoCircleOutlined className="info-icon" />
                            </Popover>
                        </Space>
                    }
                    initialValue={workingRequest?.data?.client_id}
                    rules={[
                        { required: mode === 'add', message: 'Application Client ID is required' },
                        { min: 1, message: 'Application Client ID must not be empty' },
                    ]}
                >
                    <Input
                        placeholder="abc123def456ghi789"
                        onChange={(ev) => {
                            if (!workingRequest) return
                            const update = workingRequest.clone()
                            if (!ev.target.value) {
                                delete update.data!.client_id
                            } else {
                                update.data!.client_id = ev.target.value
                            }
                            setWorkingRequest(update)
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="client_secret"
                    label={
                        <Space size={4}>
                            <span>Application Client Secret</span>
                            <Popover
                                classNames={{
                                    content: 'blueconic-editor-field-popover',
                                }}
                                content={
                                    <span>
                                        Paste the Client Secret from your BlueConic application's settings. Keep this
                                        secure.
                                    </span>
                                }
                            >
                                <InfoCircleOutlined className="info-icon" />
                            </Popover>
                        </Space>
                    }
                    rules={[
                        { required: mode === 'add', message: 'Application Client Secret is required' },
                        { min: 1, message: 'Application Client Secret must not be empty' },
                    ]}
                >
                    <Input
                        placeholder="************"
                        onChange={(ev) => {
                            if (!workingRequest) return
                            const update = workingRequest.clone()
                            if (!ev.target.value) {
                                delete update.data!.client_secret
                            } else {
                                update.data!.client_secret = ev.target.value
                            }
                            setWorkingRequest(update)
                        }}
                    />
                </Form.Item>

                <Well
                    variant="ghost"
                    padding="none"
                    className={classnames('store-id-list', {
                        scrolling: workingStores.size >= 5,
                    })}
                    title={
                        <Space size={6} className="store-id-list-title">
                            <span>Associated Content Stores</span>
                            <Typography.Text type="secondary" className="text--size--small">
                                ({workingStores.size}/{MAX_STORES})
                            </Typography.Text>
                            <Popover
                                classNames={{
                                    content: 'blueconic-editor-field-popover',
                                }}
                                content={
                                    <>
                                        <p>
                                            Add one or more BlueConic Content Stores that should be available to this
                                            integration.
                                        </p>
                                        <p>
                                            <b>Store Name</b>: Choose a display name for the Content Store. You'll
                                            select from these when creating a recommended notification.
                                        </p>
                                        <p>
                                            <b>Store ID</b>: Enter the unique ID of the Content Store in BlueConic.
                                            Contact your BlueConic representative if you can't find it.
                                        </p>
                                    </>
                                }
                            >
                                <InfoCircleOutlined className="info-icon" />
                            </Popover>
                        </Space>
                    }
                >
                    <div className="store-id-list-content">
                        {Array.from(workingStores.entries()).map(([recordId, record], idx) => {
                            const isOnlyStore = workingStores.size === 1
                            const RemoveActionComponent = isOnlyStore ? CloseCircleFilled : DeleteFilled

                            return (
                                <div key={recordId} className="store-id-row">
                                    <Form.Item
                                        layout="horizontal"
                                        name={`${recordId}_store_name`}
                                        label="Store Name"
                                        initialValue={record.record.name}
                                        rules={[
                                            { required: true, message: 'Store Name is required' },
                                            { min: 1, message: 'Store Name must not be empty' },
                                            {
                                                validator: (_, value, callback) => {
                                                    if (!isUniqueStoreName(value, recordId, workingStores)) {
                                                        callback('Store Names must be unique')
                                                    }
                                                    callback()
                                                },
                                            },
                                        ]}
                                        className="store-name-field"
                                    >
                                        <Input
                                            placeholder="Sports"
                                            ref={idx === workingStores.size - 1 ? lastStoreRecordRef : undefined}
                                            onChange={(ev) => {
                                                const updatedStoreIds: StoreIdRecordMap = new Map(workingStores)
                                                updatedStoreIds.set(recordId, {
                                                    record: BlueConicIntegrationStoreConfiguration.from({
                                                        name: ev.target.value,
                                                    }),
                                                    storeId: record.storeId,
                                                })
                                                setWorkingStores(updatedStoreIds)
                                            }}
                                            onBlur={() => {
                                                if (submitAttempted) {
                                                    validateStoreFormFields('name')
                                                }
                                            }}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        layout="horizontal"
                                        name={`${recordId}_store_id`}
                                        label="Store ID"
                                        initialValue={record.storeId}
                                        rules={[
                                            { required: true, message: 'Store ID is required' },
                                            { min: 1, message: 'Store ID must not be empty' },
                                            {
                                                validator: (_, value, callback) => {
                                                    if (!isUniqueStoreId(value, recordId, workingStores)) {
                                                        callback('Store IDs must be unique')
                                                    }
                                                    callback()
                                                },
                                            },
                                        ]}
                                        className="store-id-field"
                                    >
                                        <Input
                                            placeholder="9012-abcd-5678-efgh-12343"
                                            onChange={(ev) => {
                                                const updatedStoreIds: StoreIdRecordMap = new Map(workingStores)
                                                updatedStoreIds.set(recordId, {
                                                    record: record.record,
                                                    storeId: ev.target.value,
                                                })
                                                setWorkingStores(updatedStoreIds)
                                            }}
                                            onBlur={() => {
                                                if (submitAttempted) {
                                                    validateStoreFormFields('id')
                                                }
                                            }}
                                        />
                                    </Form.Item>

                                    <Tooltip title={isOnlyStore ? 'Clear' : 'Remove'}>
                                        <RemoveActionComponent
                                            className={classnames('store-id-row-action-rem', {
                                                'is-close-action': isOnlyStore,
                                                disabled: isOnlyStore && !record.record.name && !record.storeId,
                                            })}
                                            onClick={() => {
                                                const updatedStoreIds: StoreIdRecordMap = new Map(workingStores)
                                                if (
                                                    updatedStoreIds.size === 1 &&
                                                    !record.record.name &&
                                                    !record.storeId
                                                ) {
                                                    return
                                                }

                                                updatedStoreIds.delete(recordId)

                                                if (!updatedStoreIds.size) {
                                                    updatedStoreIds.set(getRandId(), getEmptyStoreIdRecord())
                                                }

                                                setWorkingStores(updatedStoreIds)

                                                if (submitAttempted) {
                                                    validateStoreFormFields(['id', 'name'])
                                                }
                                            }}
                                        />
                                    </Tooltip>
                                </div>
                            )
                        })}
                    </div>

                    <div className="store-id-list-actions">
                        <Button
                            ref={storesListEndRef}
                            className={classnames('store-id-list-action-add', {
                                disabled: hasMaxStoreIds,
                            })}
                            type="primary"
                            size="small"
                            disabled={hasMaxStoreIds}
                            icon={<PlusCircleFilled />}
                            onClick={() => {
                                if (hasMaxStoreIds) return

                                const updatedStoreIds: StoreIdRecordMap = new Map(workingStores)
                                updatedStoreIds.set(getRandId(), getEmptyStoreIdRecord())
                                setWorkingStores(updatedStoreIds)

                                debounceDomAction(() => {
                                    lastStoreRecordRef.current?.focus()
                                })
                            }}
                        />
                    </div>
                </Well>
            </Form>
        </PlatformIntegrationModal>
    )
}

/**
 * Returns a list of FormItem IDs for the given `stores` and `field`
 * in the format: `{record_id}_store_{field}`
 */
function getFormItemIdsFromStores(stores: StoreIdRecordMap, field: FormItemIdSuffix): string[]
function getFormItemIdsFromStores(stores: StoreIdRecordMap, fields: FormItemIdSuffix[]): string[]
function getFormItemIdsFromStores(stores: StoreIdRecordMap, fields: FormItemIdSuffix[] | FormItemIdSuffix): string[] {
    const wrappedFields = Array.isArray(fields) ? fields : [fields]

    return flatMap(Array.from(stores.keys()), (key) => wrappedFields.map((field) => `${key}_store_${field}`))
}

/**
 * Generates a {@link StoreIdRecordMap} from the `integration.data.stores` object.
 */
function extractStoreIdRecordMap(storeIds?: Record<string, BlueConicIntegrationStoreConfiguration>): StoreIdRecordMap {
    const recordMap: StoreIdRecordMap = new Map()

    Object.entries(storeIds ?? { '': BlueConicIntegrationStoreConfiguration.from({ name: '' }) }).forEach(
        ([storeId, record]) => {
            recordMap.set(getRandId(), { storeId, record })
        },
    )

    return recordMap
}

/**
 * Remaps a {@link StoreIdRecordMap} into a `Record<string, BlueConicIntegrationStoreConfiguration>`
 * for assignment to the `integration.data.stores` property.
 */
function recordFromStoreIdRecordMap(map: StoreIdRecordMap): Record<string, BlueConicIntegrationStoreConfiguration> {
    return objectFromEntries(Array.from(map.values()).map(({ storeId, record }) => [storeId, record]))
}

/**
 * Generates a new empty {@link StoreIdRecord}.
 */
function getEmptyStoreIdRecord(): StoreIdRecord {
    return {
        storeId: '',
        record: BlueConicIntegrationStoreConfiguration.from({ name: '' }),
    }
}

/**
 * Validates that the given `storeId` is unique among the given `stores`.
 */
function isUniqueStoreId(storeId: string, recordId: string, stores: StoreIdRecordMap): boolean {
    return !Array.from(stores.entries())
        .filter(([id]) => id !== recordId)
        .find(([, { storeId: otherStoreId }]) => storeId === otherStoreId)
}

/**
 * Validates that the given `storeName` is unique among the given `stores`.
 */
function isUniqueStoreName(storeName: string, recordId: string, stores: StoreIdRecordMap): boolean {
    return !Array.from(stores.entries())
        .filter(([id]) => id !== recordId)
        .find(([, { record: otherStoreRecord }]) => storeName === otherStoreRecord.name)
}

/**
 * Validates the provided {@link FormInstance} and returns a compiled {@link EditorRequest}
 * with an updated `integration.data.stores` property.
 *
 * @throws SimpleFormError
 * @throws Error
 */
async function compileSavePayload(
    mode: 'add' | 'edit',
    form: FormInstance,
    workingRequest: EditorRequest,
    workingStores: StoreIdRecordMap,
): Promise<EditorRequest> {
    // validate using antd form validation to ensure the proper
    // ux experience for the user.
    await form.validateFields()

    try {
        const update = workingRequest.clone()
        update.data!.stores = recordFromStoreIdRecordMap(workingStores)

        return update.validate()
    } catch (err) {
        if (err && 'field' in err && 'failureReason' in err) {
            err = err as InvalidFieldException | MissingFieldException
            const error = extractFriendlyErrorMessage(err, {
                fieldNamesMap: blueConicDomainIntegrationErrorFieldNamesMap,
                startWithProperty: true,
                rootModel:
                    mode === 'add' ? CreateBlueConicDomainIntegrationRequest : UpdateBlueConicDomainIntegrationRequest,
            })

            const wrappedSimpleFormError: SimpleFormError = new Error(error.message)
            wrappedSimpleFormError.errorFields = [{ errors: [error.message] }]

            throw wrappedSimpleFormError
        }

        throw err instanceof Error ? err : new Error(err)
    }
}

/* webpack-strip-block:removed */
